function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { BaseCard, CardVariant } from './BaseCard';
import { icons, spacing, themeLight, animation, breakpoints, fontStyle, mobile } from '@naf/theme';
import { Text, TextVariant } from '@naf/text';
import { ArrowForward } from '@styled-icons/material/ArrowForward';
import { propValidationConfig } from '../propValidationConfig';
export let CardSize = /*#__PURE__*/function (CardSize) {
  CardSize["Large"] = "large";
  CardSize["Medium"] = "medium";
  CardSize["Small"] = "small";
  return CardSize;
}({});
export let ColorVariant = /*#__PURE__*/function (ColorVariant) {
  ColorVariant["Signature"] = "signature";
  ColorVariant["Secondary"] = "secondary";
  ColorVariant["Outline"] = "outline";
  return ColorVariant;
}({});
export const CardNavigation = /*#__PURE__*/forwardRef(({
  title,
  illustration,
  headerTag = 'h3',
  disabled = false,
  isNotClickable = false,
  size = CardSize.Medium,
  colorVariant = ColorVariant.Outline,
  children,
  ...rest
}, ref) => {
  const sizeString = typeof size === 'string' ? size : undefined;
  const sizeFraction = typeof size === 'object' ? size : undefined;
  const hasChildren = !!children || !!illustration;
  return /*#__PURE__*/React.createElement(NavigationCard, _extends({
    variant: CardVariant.Outline,
    ref: ref,
    disabled: disabled,
    isNotClickable: isNotClickable || disabled,
    size: sizeFraction ? sizeFraction.s : sizeString,
    colorVariant: colorVariant,
    hasChildren: hasChildren
  }, rest), /*#__PURE__*/React.createElement(Content, {
    hasChildren: hasChildren,
    size: sizeString,
    small: sizeFraction && sizeFraction.s,
    medium: sizeFraction && sizeFraction.m,
    large: sizeFraction && sizeFraction.l,
    xLarge: sizeFraction && sizeFraction.xl
  }, illustration && /*#__PURE__*/React.createElement(Illustration, {
    disabled: disabled
  }, illustration), /*#__PURE__*/React.createElement(TextContent, {
    hasIllustration: !!illustration,
    size: sizeFraction ? sizeFraction.s : sizeString
  }, /*#__PURE__*/React.createElement(HeaderContainer, {
    hasChildren: hasChildren
  }, /*#__PURE__*/React.createElement(Header, {
    tag: headerTag,
    variant: TextVariant.CardHeader
  }, title), !isNotClickable && /*#__PURE__*/React.createElement(ArrowOutline, {
    showOnMobile: true,
    size: sizeFraction ? sizeFraction.s : sizeString,
    colorVariant: colorVariant
  }, /*#__PURE__*/React.createElement(ArrowForward, {
    size: icons.m
  }))), children && /*#__PURE__*/React.createElement(BodyText, {
    variant: TextVariant.BodyText
  }, children)), !isNotClickable && /*#__PURE__*/React.createElement(ArrowOutline, {
    size: sizeFraction ? sizeFraction.s : sizeString,
    colorVariant: colorVariant
  }, /*#__PURE__*/React.createElement(ArrowForward, {
    size: icons.m
  }))));
});
const HeaderContainer = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "CardNavigation__HeaderContainer",
  componentId: "sc-trpnk1-0"
})(["display:flex;justify-content:space-between;", ""], ({
  hasChildren
}) => hasChildren ? '' : css(["column-gap:", ";align-items:center;"], spacing.space16));
const sizeCssMapping = size => {
  switch (size) {
    case CardSize.Large:
      return css(["height:300px;width:300px;"]);
    case CardSize.Medium:
      return css(["height:220px;width:220px;@media (max-width:", "){height:80px;width:80px;}"], breakpoints.s);
    case CardSize.Small:
      return css(["height:160px;width:160px;@media (max-width:", "){height:80px;width:80px;}"], breakpoints.s);
    default:
      return css(["height:220px;width:220px;@media (max-width:", "){height:80px;width:80px;}"], breakpoints.s);
  }
};
const ArrowOutline = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "CardNavigation__ArrowOutline",
  componentId: "sc-trpnk1-1"
})(["display:", ";align-items:center;justify-content:center;width:", ";height:", ";min-width:", ";min-height:", ";border-radius:50%;", ";transition:", "ms ", ";@media (max-width:", "){display:", ";}"], ({
  showOnMobile
}) => showOnMobile ? 'none' : 'flex', spacing.space40, spacing.space40, spacing.space40, spacing.space40, ({
  theme,
  colorVariant,
  size
}) => {
  const activeTheme = theme.typography && theme.componentColors ? theme : themeLight;
  switch (colorVariant) {
    case ColorVariant.Signature:
      return css(["background-color:", ";svg{color:", ";}"], activeTheme.componentColors.cta.primary, activeTheme.typography.defaultText);
    case ColorVariant.Secondary:
      return css(["background-color:", ";svg{color:", ";@media (max-width:", "){color:", ";}}"], activeTheme.componentColors.cta.secondaryInverted, activeTheme.typography.brandTextInverted, breakpoints.s, size !== CardSize.Large ? activeTheme.componentColors.cta.secondaryInverted : activeTheme.typography.brandTextInverted);
    case ColorVariant.Outline:
    default:
      return css(["background-color:", ";svg{color:", ";}"], activeTheme.componentColors.cta.secondary, activeTheme.typography.defaultText);
  }
}, animation.uiSpeed, animation.easeOut, breakpoints.s, ({
  showOnMobile
}) => showOnMobile ? 'flex' : 'none');
const Illustration = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "CardNavigation__Illustration",
  componentId: "sc-trpnk1-2"
})(["align-self:center;img{height:100%;width:100%;aspect-ratio:1;}", ""], ({
  disabled
}) => disabled && css(["img{opacity:0.7;}"]));
const NavigationCard = styled(BaseCard).withConfig({
  displayName: "CardNavigation__NavigationCard",
  componentId: "sc-trpnk1-3"
})(["flex:1;width:", ";width:fit-content;min-width:0;.card{width:fit-content;margin-bottom:0;display:flex;flex-direction:column;flex-wrap:wrap;", ";", ";outline:1px solid;outline-color:", ";transition:", "ms ", ";border:none;}&:hover{", "}", ";@media (max-width:", "){.card{padding-bottom:", ";}&:hover{.card{outline:1px solid;outline-color:", ";", "}}}"], props => props.width && typeof props.width === 'number' ? `${props.width}px` : props.width, ({
  colorVariant,
  theme
}) => colorVariant === ColorVariant.Secondary && css(["background-color:", ";"], theme.background ? theme.background.brand : themeLight.background.brand), ({
  colorVariant,
  theme
}) => colorVariant === ColorVariant.Signature && css(["background-color:", ";"], theme.background ? theme.background.important : themeLight.background.important), ({
  theme,
  colorVariant
}) => {
  const activeTheme = theme.background && theme.border ? theme : themeLight;
  switch (colorVariant) {
    case ColorVariant.Signature:
      return activeTheme.background.important;
    case ColorVariant.Secondary:
      return activeTheme.background.brand;
    case ColorVariant.Outline:
    default:
      return activeTheme.border.subtle;
  }
}, animation.uiSpeed, animation.easeOut, ({
  isNotClickable,
  hasChildren
}) => !isNotClickable && css([".card{outline:3px solid;outline-color:", ";transition:", "ms ", ";}", "{background-color:", ";", ";", ";@media (max-width:", "){margin-left:", ";margin-right:0;}transition:", "ms ", ";}"], ({
  theme,
  colorVariant
}) => {
  const activeTheme = theme.componentColors && theme.border ? theme : themeLight;
  switch (colorVariant) {
    case ColorVariant.Signature:
      return activeTheme.componentColors.cta.primaryHover;
    case ColorVariant.Secondary:
      return activeTheme.componentColors.cta.secondaryInvertedHover;
    case ColorVariant.Outline:
    default:
      return activeTheme.border.subtle;
  }
}, animation.uiSpeed, animation.easeIn, ArrowOutline, ({
  theme,
  colorVariant
}) => {
  const activeTheme = theme.componentColors ? theme : themeLight;
  switch (colorVariant) {
    case ColorVariant.Signature:
      return activeTheme.componentColors.cta.primary;
    case ColorVariant.Secondary:
      return activeTheme.componentColors.cta.secondaryInverted;
    case ColorVariant.Outline:
    default:
      return activeTheme.componentColors.cta.secondaryHover;
  }
}, hasChildren && css(["margin-left:", ";margin-right:0;"], spacing.space16), !hasChildren && css(["margin-left:", ";margin-right:0;"], spacing.space8), breakpoints.s, spacing.space8, animation.uiSpeed, animation.easeIn), ({
  disabled
}) => disabled && css(["pointer-events:none;color:", ";.card{background-color:", ";}", "{background-color:transparent;}"], ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.componentColors ? theme.componentColors.cta.disabled : themeLight.componentColors.cta.disabled, ArrowOutline), breakpoints.s, spacing.space16, ({
  theme
}) => theme.border ? theme.border.subtle : themeLight.border.subtle, ({
  size
}) => size !== CardSize.Large && css(["", "{background-color:transparent;transition:0s;}"], ArrowOutline));
const Header = styled(Text).withConfig({
  displayName: "CardNavigation__Header",
  componentId: "sc-trpnk1-4"
})(["margin:0;"]);
const TextContent = styled.span.withConfig(propValidationConfig).withConfig({
  displayName: "CardNavigation__TextContent",
  componentId: "sc-trpnk1-5"
})(["display:inline-flex;flex:1;flex-direction:column;max-width:100%;", " ", ""], ({
  hasIllustration
}) => hasIllustration && css(["margin-top:", ";"], spacing.space24), ({
  hasIllustration,
  size
}) => hasIllustration && size !== CardSize.Large && css(["@media (max-width:", "){margin-left:", ";margin-top:0;min-width:0;}"], breakpoints.s, spacing.space16));
const BodyText = styled(Text).withConfig({
  displayName: "CardNavigation__BodyText",
  componentId: "sc-trpnk1-6"
})(["margin:", " 0 0;"], spacing.space8);
const Content = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "CardNavigation__Content",
  componentId: "sc-trpnk1-7"
})(["display:flex;align-items:center;width:100%;height:100%;", "{margin-right:", ";margin-left:0;}", " ", " ", "{", ";", " ", " ", " ", "}", "{", " ", " ", " ", " ", "}"], ArrowOutline, spacing.space8, ({
  hasChildren,
  size
}) => hasChildren ? css(["flex-direction:column;align-items:flex-start;flex-wrap:wrap;height:100%;", "{margin-top:", ";margin-right:0;margin-left:0;@media (max-width:", "){margin-top:", ";margin-left:", ";}}", "{margin-right:0;}"], ArrowOutline, spacing.space24, breakpoints.s, size === CardSize.Large ? '0px' : `${spacing.space24}`, spacing.space8, TextContent) : css(["column-gap:", ";"], spacing.space16), ({
  size,
  small
}) => size !== CardSize.Large && small !== CardSize.Large && css(["@media (max-width:", "){flex-direction:row;flex-wrap:nowrap;", "{height:fit-content;width:fit-content;background-color:transparent;margin-top:0;margin-left:", ";}}"], breakpoints.s, ArrowOutline, spacing.space16), Illustration, ({
  size
}) => size && sizeCssMapping(size), ({
  xLarge
}) => xLarge && css(["@media (", " < width){", ";}"], breakpoints.l, sizeCssMapping(xLarge)), ({
  large
}) => large && css(["@media (", " < width <= ", "){", ";}"], breakpoints.m, breakpoints.l, sizeCssMapping(large)), ({
  medium
}) => medium && css(["@media (", " < width <= ", "){", ";}"], breakpoints.s, breakpoints.m, sizeCssMapping(medium)), ({
  small
}) => small && css(["@media (max-width:", "){", ";}"], breakpoints.s, sizeCssMapping(small)), Header, ({
  size
}) => size === CardSize.Large && css(["", ";@media (max-width:", "){", ";}"], fontStyle.headers.header2, breakpoints.s, mobile.headers.header2), ({
  xLarge
}) => xLarge === CardSize.Large && css(["@media (", " < width){", ";}"], breakpoints.l, fontStyle.headers.header2), ({
  large
}) => large === CardSize.Large && css(["@media (", " < width <= ", "){", ";}"], breakpoints.m, breakpoints.l, fontStyle.headers.header2), ({
  medium
}) => medium === CardSize.Large && css(["@media (", " < width <= ", "){", ";}"], breakpoints.s, breakpoints.m, fontStyle.headers.header2), ({
  small
}) => small === CardSize.Large && css(["@media (max-width:", "){", ";}"], breakpoints.s, mobile.headers.header2));
//# sourceMappingURL=CardNavigation.js.map